// Fiche d'une tournée : plan de la tournée (secteur, jour, heure, opérateur, véhicule, points ordonnés,
// version), saisie d'une réalisation (réalisée, partielle avec points collectés, non réalisée avec motif),
// réalisations passées, opérations reçues de la synchronisation terrain (appliquées, doublons, conflits),
// suspension ou reprise par le responsable.
import { CODES_ETATS_REALISATION, ETATS_REALISATION, schemaRealisationTournee } from '@cc/contrats';
import { Bandeau, Bouton, CaseACocher, Champ, LienBouton, Panneau, PuceEtat, Selection, Tableau, ZoneTexte, type ColonneTableau } from '@cc/ui';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { enregistrerRealisation, INVALIDER_PROPRETE, modifierTournee, useTournee } from './api';
import { aujourdhuiIso, libelleJour, resumePoints, TON_REALISATION } from './logique';
import type { EtatRealisation, Realisation, TourneeDetail } from './types';
import styles from './proprete.module.css';
import commun from '../../commun/commun.module.css';

const LIBELLES_ORIGINE = { bureau: 'Saisie au bureau', synchronisation: 'Reçue de l\'appareil' } as const;
const LIBELLES_RESULTAT: Record<string, string> = { appliquee: 'Appliquée', ignoree_doublon: 'Doublon ignoré', conflit: 'Conflit', rejetee: 'Rejetée' };

function SaisieRealisation({ t }: { t: TourneeDetail }) {
  const [v, setV] = useState({ date: aujourdhuiIso(), etat: 'realisee' as EtatRealisation, motif: '', points: [] as string[], observations: '' });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const action = useAction({
    executer: () => {
      const corps = schemaRealisationTournee.safeParse({ date: v.date, etat: v.etat, motif: v.motif || undefined, points_collectes: v.etat === 'partielle' ? v.points : [], observations: v.observations || undefined });
      if (!corps.success) {
        setErreurs(Object.fromEntries(corps.error.issues.map((i) => [String(i.path[0]), i.message])));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreurs({});
      return enregistrerRealisation(t.id, corps.data);
    },
    succes: (r) => `Réalisation du ${date(r.date)} enregistrée : ${r.etat_libelle}.`,
    invalider: INVALIDER_PROPRETE,
    apres: () => setV((x) => ({ ...x, motif: '', points: [], observations: '' })),
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message ?? erreurs[nom];
  return (
    <Panneau titre="Saisir une réalisation" niveau={2}>
      <div className={styles.grilleFormulaire}>
        <Champ libelle="Date" type="date" value={v.date} onChange={(e) => setV((x) => ({ ...x, date: e.target.value }))} erreur={champ('date')} required />
        <Selection libelle="Résultat" options={CODES_ETATS_REALISATION.map((e) => ({ valeur: e, libelle: ETATS_REALISATION[e] }))} value={v.etat} onChange={(e) => setV((x) => ({ ...x, etat: e.target.value as EtatRealisation }))} />
      </div>
      {v.etat === 'non_realisee' ? <Champ libelle="Motif de la non-réalisation" value={v.motif} onChange={(e) => setV((x) => ({ ...x, motif: e.target.value }))} erreur={champ('motif')} required /> : null}
      {v.etat === 'partielle' ? (
        <fieldset>
          <legend className="cc-etiquette">Points collectés ({v.points.length} sur {t.points_detail.length})</legend>
          {champ('points_collectes') ? <p className={styles.motif}>{champ('points_collectes')}</p> : null}
          <ul className={styles.pointsListe}>
            {t.points_detail.map((p) => (
              <li key={p.id}>
                <span className={styles.ordre}>{p.ordre}</span>
                <CaseACocher libelle={`${p.code} · ${p.nom}`} checked={v.points.includes(p.id)} onChange={() => setV((x) => ({ ...x, points: x.points.includes(p.id) ? x.points.filter((q) => q !== p.id) : [...x.points, p.id] }))} />
              </li>
            ))}
          </ul>
        </fieldset>
      ) : null}
      <ZoneTexte libelle="Observations" value={v.observations} onChange={(e) => setV((x) => ({ ...x, observations: e.target.value }))} rows={2} facultatif />
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      <Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>Enregistrer la réalisation</Bouton>
    </Panneau>
  );
}

export function PageTournee() {
  const { id = '' } = useParams();
  const { contexte } = useSession();
  const fiche = useTournee(id);
  const t = fiche.data;
  const suspension = useAction({
    executer: (actif: boolean) => modifierTournee(id, { actif }),
    succes: (r) => (r.actif ? 'Tournée réactivée.' : 'Tournée suspendue : elle ne figure plus au calendrier.'),
    invalider: INVALIDER_PROPRETE,
  });
  const colonnes: ColonneTableau<Realisation>[] = [
    { id: 'date', entete: 'Date', enTeteLigne: true, masquable: false, cellule: (r) => date(r.date), valeurTri: (r) => r.date },
    { id: 'etat', entete: 'Résultat', cellule: (r) => <PuceEtat ton={TON_REALISATION[r.etat]} libelle={r.etat_libelle} />, valeurTri: (r) => r.etat },
    { id: 'points', entete: 'Points', cellule: (r) => resumePoints(r) },
    { id: 'motif', entete: 'Motif et observations', cellule: (r) => [r.motif, r.observations].filter(Boolean).join(' ; ') || 'aucun' },
    { id: 'origine', entete: 'Origine', cellule: (r) => `${LIBELLES_ORIGINE[r.origine]}${r.enregistre_sur_appareil_le ? `, enregistrée le ${dateHeure(r.enregistre_sur_appareil_le)}` : ''}` },
    { id: 'recu', entete: 'Reçue le', cellule: (r) => dateHeure(r.recu_le), valeurTri: (r) => r.recu_le },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Déchets et propreté"
        titre={t ? `Tournée ${t.code} · ${t.nom}` : 'Tournée'}
        description={t ? `${libelleJour(t.jour_semaine)} à ${t.heure}, secteur ${t.secteur.nom ?? t.secteur.code ?? ''}, ${t.points.length} point${t.points.length > 1 ? 's' : ''} de collecte, version ${t.version}.` : null}
        actions={
          <>
            <LienBouton href="/proprete" icone={ArrowLeft} variante="secondaire">Tournées</LienBouton>
            {t && peut(contexte, 'dechets', 'modifier') ? (
              <Bouton variante="secondaire" chargement={suspension.enCours} onClick={() => suspension.lancer(!t.actif)}>{t.actif ? 'Suspendre la tournée' : 'Réactiver la tournée'}</Bouton>
            ) : null}
          </>
        }
      />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} lot="L3-B" fonction="La fiche de la tournée">
        {t ? (
          <div className={commun.deuxColonnes}>
            <div className={commun.pile}>
              <Panneau titre="Plan de la tournée">
                <dl className={commun.definitions}>
                  <dt>Opérateur</dt>
                  <dd>{t.operateur === 'prestataire' ? `${t.operateur_libelle}${t.prestataire?.nom ? ` : ${t.prestataire.nom}` : ''}` : t.operateur_libelle}</dd>
                  <dt>Équipe</dt>
                  <dd>{t.equipe?.nom ?? 'non affectée'}</dd>
                  <dt>Véhicule</dt>
                  <dd>{t.vehicule ? `${t.vehicule.code ?? ''} ${t.vehicule.immatriculation ?? ''}`.trim() : 'non affecté'}</dd>
                  <dt>État</dt>
                  <dd>{t.actif ? <PuceEtat ton="ok" libelle="Active" /> : <PuceEtat ton="neutre" libelle="Suspendue" />}</dd>
                  <dt>Dernière réalisation</dt>
                  <dd>{t.derniere_realisation ? `${date(t.derniere_realisation.date)}, ${ETATS_REALISATION[t.derniere_realisation.etat]}` : 'aucune'}</dd>
                </dl>
                <h3>Points dans l&apos;ordre de passage</h3>
                {t.points_detail.length === 0 ? <p className={commun.na}>Aucun point de collecte.</p> : null}
                <ol className={styles.pointsListe}>
                  {t.points_detail.map((p) => (
                    <li key={p.id}>
                      <span className={styles.ordre}>{p.ordre}</span>
                      <span>
                        <span className={commun.reference}>{p.code}</span> {p.nom}
                      </span>
                    </li>
                  ))}
                </ol>
              </Panneau>
              {peut(contexte, 'dechets', 'creer') && t.actif ? <SaisieRealisation t={t} /> : null}
            </div>
            <div className={commun.pile}>
              <Panneau titre={`Réalisations (${t.realisations.length})`}>
                {t.realisations.length === 0 ? <p className={commun.na}>Aucune réalisation enregistrée.</p> : <Tableau legende="Réalisations de la tournée" colonnes={colonnes} lignes={t.realisations} cleLigne={(r) => r.id} cleColonnes="proprete-realisations-tournee" />}
              </Panneau>
              <Panneau titre={`Opérations reçues de la synchronisation (${t.operations.length})`} niveau={3}>
                {t.operations.length === 0 ? <p className={commun.na}>Aucune opération reçue d&apos;un appareil pour cette tournée.</p> : null}
                <ul className={styles.pointsListe}>
                  {t.operations.map((o) => (
                    <li key={o.id}>
                      <PuceEtat ton={o.resultat === 'appliquee' ? 'ok' : o.resultat === 'conflit' ? 'retard' : 'neutre'} libelle={LIBELLES_RESULTAT[o.resultat] ?? o.resultat} />
                      <span>
                        {dateHeure(o.recu_le)}
                        {o.motif ? ` : ${o.motif}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </Panneau>
            </div>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
